import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Flag, Trash2, X, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * SharedExperiences — one-level responses beneath an Ask the Collective
 * question. These are NOT comments and NOT replies; the structure is
 * intentionally flat so the Collective gathers perspectives rather than
 * generating argument threads.
 *
 * Shape per question:
 *   question
 *   ├─ shared experience · shared experience · shared experience
 *   (no nested replies, no @mentions, no quote-replies)
 *
 * Reactions limited to a single "Helpful ♡". Reports flow into the
 * existing moderation queue — no new UI layer required.
 */

export default function SharedExperiences({ post, selfUserId }) {
  const [experiences, setExperiences] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await endpoints.sharedExperiencesList(post.id);
      setExperiences(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [post.id]);

  const count = experiences.length;

  return (
    <section
      className="mt-5 pt-5 border-t hairline"
      data-testid={`shared-experiences-${post.id}`}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--terracotta))]">
            {count === 0 ? "Shared Experiences" : `${count} Shared Experience${count === 1 ? "" : "s"}`}
          </div>
          <p className="mt-1 text-[12px] italic text-[hsl(var(--muted-foreground))]">
            Share what you&apos;ve lived, not what you sell.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          data-testid={`share-experience-open-${post.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline text-sm tab-link hover:bg-[hsl(var(--sand))]"
        >
          Share your experience
        </button>
      </div>

      {loaded && experiences.length === 0 ? (
        <p
          className="mt-4 text-[13px] text-[hsl(var(--muted-foreground))] italic"
          data-testid={`shared-experiences-empty-${post.id}`}
        >
          No one has shared an experience here yet. Be the first — perspective lands softly.
        </p>
      ) : null}

      <ul className="mt-4 space-y-5">
        {experiences.map((exp) => (
          <SharedExperienceRow
            key={exp.id}
            exp={exp}
            isMine={exp.author_user_id === selfUserId}
            onChanged={load}
            onReport={() => setReportTarget(exp)}
          />
        ))}
      </ul>

      <ShareExperienceComposer
        post={post}
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onPosted={load}
      />
      <ReportDialog
        target={reportTarget}
        onClose={() => setReportTarget(null)}
      />
    </section>
  );
}

function SharedExperienceRow({ exp, isMine, onChanged, onReport }) {
  const [busy, setBusy] = useState(false);
  // Soft recognition — when a Shared Experience has quietly resonated with
  // several members, surface a subtle marker so the perspective is easier
  // to notice without reshuffling the chronological order. Purely a label;
  // no ranking, no sort, no "top answer" state.
  const resonated = (exp.helpful_count || 0) >= 3;
  const toggleHelpful = async () => {
    setBusy(true);
    try {
      await endpoints.sharedExperienceHelpful(exp.id);
      onChanged?.();
    } catch { toast.error("Couldn't register that."); }
    finally { setBusy(false); }
  };
  const removeMine = async () => {
    if (!window.confirm("Remove your Shared Experience?")) return;
    setBusy(true);
    try {
      await endpoints.sharedExperienceDelete(exp.id);
      toast("Removed.");
      onChanged?.();
    } catch { toast.error("Couldn't remove."); }
    finally { setBusy(false); }
  };

  return (
    <li
      className="relative"
      data-testid={`shared-experience-${exp.id}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--clay))]">
        <span data-testid={`shared-experience-author-${exp.id}`}>{exp.author}</span>
        {exp.is_professional && (
          <span className="px-2 py-0.5 rounded-full border hairline text-[9px] tracking-[0.2em]">
            Professional
          </span>
        )}
        {resonated && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--terracotta))]/12 text-[9px] tracking-[0.2em] text-[hsl(var(--terracotta))]"
            data-testid={`shared-experience-resonated-${exp.id}`}
            title="A perspective several members have found helpful"
          >
            <Sparkles size={9} />
            Helpful to the Collective
          </span>
        )}
      </div>
      <p
        className="mt-1.5 font-display italic text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--fg))]/92"
        data-testid={`shared-experience-content-${exp.id}`}
      >
        {exp.content}
      </p>
      <div className="mt-2 flex items-center gap-4 text-[11px] text-[hsl(var(--muted-foreground))]">
        <button
          type="button"
          onClick={toggleHelpful}
          disabled={busy}
          aria-pressed={exp.your_helpful}
          data-testid={`shared-experience-helpful-${exp.id}`}
          className={`inline-flex items-center gap-1 tab-link ${
            exp.your_helpful
              ? "text-[hsl(var(--terracotta))]"
              : "hover:text-[hsl(var(--terracotta))]"
          }`}
        >
          <Heart size={12} fill={exp.your_helpful ? "currentColor" : "none"} />
          Helpful {exp.helpful_count > 0 ? `· ${exp.helpful_count}` : ""}
        </button>
        {!isMine && (
          <button
            type="button"
            onClick={onReport}
            data-testid={`shared-experience-report-${exp.id}`}
            className="inline-flex items-center gap-1 tab-link hover:text-[hsl(var(--clay))]"
          >
            <Flag size={11} /> Report
          </button>
        )}
        {isMine && (
          <button
            type="button"
            onClick={removeMine}
            disabled={busy}
            data-testid={`shared-experience-delete-${exp.id}`}
            className="inline-flex items-center gap-1 tab-link hover:text-[hsl(var(--clay))]"
          >
            <Trash2 size={11} /> Remove
          </button>
        )}
      </div>
    </li>
  );
}

function ShareExperienceComposer({ post, open, onOpenChange, onPosted }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [blockReason, setBlockReason] = useState(null);

  const submit = async () => {
    const content = text.trim();
    if (!content) return;
    setPosting(true);
    setBlockReason(null);
    try {
      await endpoints.sharedExperienceCreate(post.id, content);
      toast.success("Shared.");
      setText("");
      onOpenChange(false);
      onPosted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't share that right now.";
      setBlockReason(detail);
    } finally { setPosting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setBlockReason(null); }}>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-lg" data-testid={`share-experience-dialog-${post.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Share your experience
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--muted-foreground))]">
            Share what you&apos;ve lived, learned, or experienced. The Collective is for
            perspective — not prescribing or promotion.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={2000}
          data-testid={`share-experience-textarea-${post.id}`}
          placeholder="What worked for you, what you noticed, what you learned…"
          className="min-h-[140px]"
        />
        <div className="text-[11px] text-[hsl(var(--muted-foreground))] text-right">
          {text.length}/2000
        </div>
        {blockReason && (
          <div
            className="rounded-md border hairline bg-[hsl(var(--terracotta))]/10 p-3 text-[13px] leading-relaxed text-[hsl(var(--fg))]/90"
            data-testid={`share-experience-block-${post.id}`}
          >
            <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
              This looks promotional
            </div>
            <p className="mt-1">{blockReason}</p>
            <p className="mt-2 text-[11px] italic text-[hsl(var(--muted-foreground))]">
              Edit the response and try again.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid={`share-experience-cancel-${post.id}`}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={posting || !text.trim()}
            data-testid={`share-experience-submit-${post.id}`}
            className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
          >
            {posting ? "Sharing…" : "Share Experience"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REPORT_OPTIONS = [
  { slug: "solicitation",  label: "Solicitation / Promotion" },
  { slug: "medical",       label: "Medical or Unsafe Advice" },
  { slug: "harassment",    label: "Harassment" },
  { slug: "spam",          label: "Spam" },
  { slug: "inappropriate", label: "Inappropriate Content" },
  { slug: "other",         label: "Other" },
];

function ReportDialog({ target, onClose }) {
  const [category, setCategory] = useState("solicitation");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await endpoints.sharedExperienceReport(target.id, category, note || undefined);
      toast.success("Reported. A moderator will review.");
      onClose();
      setNote("");
    } catch { toast.error("Couldn't send the report."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-md" data-testid="shared-experience-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Report this Shared Experience</DialogTitle>
          <DialogDescription className="text-[hsl(var(--muted-foreground))]">
            A moderator will review and decide. Thank you for helping keep the Collective kind.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {REPORT_OPTIONS.map((o) => (
            <label
              key={o.slug}
              className="flex items-center gap-2 text-sm cursor-pointer"
              data-testid={`shared-experience-report-opt-${o.slug}`}
            >
              <input
                type="radio"
                name="report-category"
                value={o.slug}
                checked={category === o.slug}
                onChange={() => setCategory(o.slug)}
              />
              {o.label}
            </label>
          ))}
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={600}
          placeholder="Anything else the moderator should know? (optional)"
          data-testid="shared-experience-report-note"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full"><X size={14}/> Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="shared-experience-report-submit"
            className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
          >
            {busy ? "Reporting…" : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
